import * as React from "react";
import { linksOf, request } from "../../services/cpln";
import { Workload } from "../../schema/types/workload/entity";
import { Deployment } from "../../schema/types/workload/deployment";
import { Command, RunCronWorkloadStatus } from "../../schema/types/command";
import { GlobalJobExecution, FetchProgress, TriggerType, JobExecutionStatus } from "./types";
import { loadCache, saveCache, clearCache, getCacheAge } from "./cache";
import { parseLink } from "../../utils/linkParser/linkParser";

const BATCH_SIZE = 10;

interface UseGlobalJobExecutionsReturn {
  executions: GlobalJobExecution[];
  isLoading: boolean;
  error: string | null;
  progress: FetchProgress;
  cacheAge: string;
  totalWorkloads: number;
  fetchExecutions: () => Promise<void>;
  clearLocalCache: () => void;
}

export function useGlobalJobExecutions(orgName: string): UseGlobalJobExecutionsReturn {
  const [executions, setExecutions] = React.useState<GlobalJobExecution[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [progress, setProgress] = React.useState<FetchProgress>({
    phase: "idle",
    current: 0,
    total: 0,
    message: "",
  });
  const [cacheAge, setCacheAge] = React.useState("Never");
  const [totalWorkloads, setTotalWorkloads] = React.useState(0);

  // Load from cache on mount
  React.useEffect(() => {
    const cache = loadCache(orgName);
    if (cache) {
      setExecutions(cache.executions);
      setTotalWorkloads(cache.cronWorkloadLinks.length);
      setCacheAge(getCacheAge(cache));
    }
  }, [orgName]);

  // Update cache age periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      const cache = loadCache(orgName);
      setCacheAge(getCacheAge(cache));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [orgName]);

  const fetchAllWorkloadsPaginated = React.useCallback(async (): Promise<Workload[]> => {
    const items: Workload[] = [];
    let nextLink: string | undefined = `/org/${orgName}/workload`;

    setProgress({ phase: "workloads", current: 0, total: 0, message: "Loading workloads..." });

    while (nextLink) {
      const { data } = await request({ url: nextLink });
      items.push(...(data.items || []));
      nextLink = linksOf(data).next;
      setProgress({
        phase: "workloads",
        current: items.length,
        total: 0,
        message: `Loaded ${items.length} workloads...`,
      });
    }

    return items;
  }, [orgName]);

  const fetchWorkloadExecutions = React.useCallback(
    async (workload: Workload): Promise<GlobalJobExecution[]> => {
      const workloadLink = linksOf(workload).self!;
      const { gvc: gvcName } = parseLink(workloadLink, { useInputCtx: true });
      const gvcLink = `/org/${orgName}/gvc/${gvcName}`;

      // Fetch deployments and commands in parallel
      const [deploymentsResponse, commandsResponse] = await Promise.all([
        request({ url: `${workloadLink}/deployment` }).catch(() => ({ data: { items: [] } })),
        request({ url: `${workloadLink}/-command` }).catch(() => ({ data: { items: [] } })),
      ]);

      const deployments: Deployment[] = deploymentsResponse.data?.items || [];
      const commands: Command[] = commandsResponse.data?.items || [];

      const executions: GlobalJobExecution[] = [];

      for (const deployment of deployments) {
        const deploymentExecutions = (deployment.status?.jobExecutions as any[]) || [];

        for (const exec of deploymentExecutions) {
          const commandId = exec.name?.startsWith("command-") ? exec.name.split("command-")[1] : undefined;
          const command = commandId ? commands.find((c) => c.id === commandId) : undefined;
          const isOnDemand = commandId ? !command?.tags?.["cpln/owner"] : false;
          let displayName = exec.name;

          if (command?.tags?.name) {
            displayName = command.tags.name;
          }

          executions.push({
            id: `${workload.name}-${deployment.name}-${exec.name}`,
            name: exec.name,
            displayName,
            triggerType: (isOnDemand ? "on-demand" : "scheduled") as TriggerType,
            status: exec.status as JobExecutionStatus,
            location: deployment.name,
            replica: exec.replica,
            startTime: exec.startTime,
            completionTime: exec.completionTime,
            commandId,
            message: exec.message,
            containers: exec.containers || {},
            workloadVersion: exec.workloadVersion || 0,
            workloadName: workload.name,
            workloadLink,
            gvcName,
            gvcLink,
          });
        }

        // Add in-flight commands that haven't appeared in jobExecutions yet
        const inFlightCommands = commands.filter(
          (c) =>
            c.type === "runCronWorkload" &&
            c.spec?.location === deployment.name &&
            !!c.lifecycleStage &&
            ["running", "pending", "failed"].includes(c.lifecycleStage) &&
            !deploymentExecutions.some((e) => e.name === `command-${c.id}`),
        );

        for (const cmd of inFlightCommands) {
          const messages = (cmd.status as RunCronWorkloadStatus)?.messages || [];
          const containers: Record<string, any> = {};
          messages.forEach((message, idx) => {
            containers[`cpln-ui-${idx}`] = { message };
          });

          executions.push({
            id: `${workload.name}-${deployment.name}-command-${cmd.id}`,
            name: `command-${cmd.id}`,
            displayName: cmd.tags?.name || `command-${cmd.id}`,
            triggerType: cmd.tags?.["cpln/owner"] ? "scheduled" : "on-demand",
            status: (cmd.lifecycleStage === "running" ? "active" : cmd.lifecycleStage) as JobExecutionStatus,
            location: deployment.name,
            replica: cmd.status?.replica,
            startTime: cmd.created,
            completionTime: undefined,
            commandId: cmd.id,
            containers,
            workloadVersion: 0,
            workloadName: workload.name,
            workloadLink,
            gvcName,
            gvcLink,
          });
        }
      }

      return executions;
    },
    [orgName],
  );

  const fetchExecutions = React.useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const cache = loadCache(orgName);

      // Fetch ALL workloads and filter for type=cron client-side
      const allWorkloads = await fetchAllWorkloadsPaginated();
      const cronWorkloads = allWorkloads.filter((w) => w.spec?.type === "cron");
      const currentLinks = cronWorkloads.map((w) => linksOf(w).self!);

      setTotalWorkloads(cronWorkloads.length);

      if (cronWorkloads.length === 0) {
        setExecutions([]);
        saveCache(orgName, {
          lastFetched: new Date().toISOString(),
          workloadsFetched: [],
          executions: [],
          cronWorkloadLinks: [],
        });
        setCacheAge(getCacheAge(loadCache(orgName)));
        setProgress({ phase: "idle", current: 0, total: 0, message: "" });
        setIsLoading(false);
        return;
      }

      setProgress({
        phase: "executions",
        current: 0,
        total: cronWorkloads.length,
        message: `Loading executions for ${cronWorkloads.length} workloads...`,
      });

      // Batch fetch workload executions for ALL cron workloads
      const newExecutions: GlobalJobExecution[] = [];
      for (let i = 0; i < cronWorkloads.length; i += BATCH_SIZE) {
        const batch = cronWorkloads.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(batch.map(fetchWorkloadExecutions));
        newExecutions.push(...batchResults.flat());

        setProgress({
          phase: "executions",
          current: Math.min(i + BATCH_SIZE, cronWorkloads.length),
          total: cronWorkloads.length,
          message: `Loaded executions for ${Math.min(i + BATCH_SIZE, cronWorkloads.length)} of ${
            cronWorkloads.length
          } workloads...`,
        });
      }

      // Merge: new data takes precedence, but keep old executions that aren't in the new fetch
      // (e.g., executions from workloads that were deleted but we want to preserve history)
      const newExecutionIds = new Set(newExecutions.map((e) => e.id));
      const oldExecutionsToKeep = (cache?.executions || []).filter(
        (e) => !newExecutionIds.has(e.id) && currentLinks.includes(e.workloadLink),
      );
      const finalExecutions = [...newExecutions, ...oldExecutionsToKeep];

      // Save to cache
      saveCache(orgName, {
        lastFetched: new Date().toISOString(),
        workloadsFetched: currentLinks,
        executions: finalExecutions,
        cronWorkloadLinks: currentLinks,
      });

      setExecutions(finalExecutions);
      setCacheAge(getCacheAge(loadCache(orgName)));
      setProgress({ phase: "idle", current: 0, total: 0, message: "" });
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e.message || "Failed to fetch job executions";
      setError(errorMessage);
      setProgress({ phase: "idle", current: 0, total: 0, message: "" });
    } finally {
      setIsLoading(false);
    }
  }, [orgName, isLoading, fetchAllWorkloadsPaginated, fetchWorkloadExecutions]);

  const clearLocalCache = React.useCallback(() => {
    clearCache(orgName);
    setExecutions([]);
    setTotalWorkloads(0);
    setCacheAge("Never");
  }, [orgName]);

  return {
    executions,
    isLoading,
    error,
    progress,
    cacheAge,
    totalWorkloads,
    fetchExecutions,
    clearLocalCache,
  };
}
